import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, Sparkles, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UserProfilePopup
 * Triggered when the user clicks their info card in the sidebar.
 * Shows: name, role, current org/venue (if any), and a code-redemption box.
 */
export default function UserProfilePopup({ user, open, onOpenChange, onJoined }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleRedeem = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim().toUpperCase().replace(/\s/g, "");
    if (!trimmed) {
      toast.error("Enter the code your manager sent you");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/org/redeem-code`, { code: trimmed });
      // Stash a one-time flag — the dashboard reads this on mount and shows the welcome overlay.
      try {
        localStorage.setItem("fs_just_joined_venue", JSON.stringify({
          venue_name: res.data.venue_name,
          org_name: res.data.org_name,
          ts: Date.now(),
        }));
      } catch {
        // localStorage disabled (private mode) — toast is still a fine fallback
      }
      toast.success(`Welcome to ${res.data.venue_name}!`);
      setCode("");
      onOpenChange(false);
      // Tell the parent to refresh the user object — content visible to them just changed
      if (onJoined) onJoined(res.data);
      else window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "That code didn't work");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  const initial = user.name?.charAt(0).toUpperCase() || "?";
  const roleLabel = ({
    system_admin: "System Admin",
    org_admin: "Org Admin",
    venue_manager: "Venue Manager",
    admin: "Admin",
    staff: "Staff",
  })[user.role] || "Member";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="user-profile-popup">
        <DialogHeader>
          <DialogTitle className="sr-only">Your profile</DialogTitle>
          <DialogDescription className="sr-only">View your account info and join a venue with a code.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 pb-2">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/30 flex items-center justify-center">
              <span className="text-[hsl(var(--primary))] text-xl font-bold">{initial}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-bold text-lg truncate" data-testid="profile-name">{user.name}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user.email}</p>
            <span className="inline-block mt-1 text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 px-2 py-0.5 rounded-full" data-testid="profile-role">{roleLabel}</span>
          </div>
        </div>

        {(user.org_name || user.venue_name) && (
          <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30 p-3 space-y-1.5">
            {user.org_name && (
              <p className="text-xs flex items-center gap-2"><Building2 size={12} className="text-[hsl(var(--primary))]" /> <span className="text-[hsl(var(--muted-foreground))]">Organisation:</span> <span className="font-medium">{user.org_name}</span></p>
            )}
            {user.venue_name && (
              <p className="text-xs flex items-center gap-2"><MapPin size={12} className="text-[hsl(var(--primary))]" /> <span className="text-[hsl(var(--muted-foreground))]">Venue:</span> <span className="font-medium">{user.venue_name}</span></p>
            )}
          </div>
        )}

        {/* Redeem code — shown to everyone (lets users switch venues / join a team) */}
        <form onSubmit={handleRedeem} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/20 p-4 space-y-3" data-testid="redeem-code-form">
          <div className="flex items-center gap-2">
            <KeyRound size={14} className="text-[hsl(var(--primary))]" />
            <p className="text-sm font-bold">Have an invite code?</p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Paste the code your manager sent you to join their team.
          </p>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD-1234"
              autoCapitalize="characters"
              className="font-mono tracking-wider bg-[hsl(var(--background))]"
              data-testid="redeem-code-input"
              maxLength={20}
            />
            <Button type="submit" disabled={submitting || !code.trim()} className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90" data-testid="redeem-code-submit">
              {submitting ? "Joining…" : "Join"}
            </Button>
          </div>
          {user.role === "system_admin" && (
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Sparkles size={10} /> System admins can join any org with a code to curate their content.</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
